import React, { useEffect, useState } from "react";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { CMSData } from "@/cms/models";
import { ZipCode, ZipCodeGroup } from "@/types/zipcode";

const ZipCodeInput: React.FC<{
  selectedZipCodes: (ZipCode | ZipCodeGroup)[];
  errors: string[] | undefined;
  cmsData: CMSData;
  children: React.ReactNode;
  onChange: (items: (ZipCode | ZipCodeGroup)[]) => void;
  zipCodeGroups: ZipCodeGroup[];
}> = ({ onChange, cmsData, errors, selectedZipCodes, zipCodeGroups }) => {
  const style = cmsData.forms.style;
  const [zipCodes, setZipCodes] = useState<(ZipCode | ZipCodeGroup)[]>([]);
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    if (query.length <= 2) {
      setZipCodes([]);
    } else {
      const fetchData = async () => {
        const response = await axios.get<any[]>(
          `https://api.dataforsyningen.dk/postnumre?q=${query}*`
        );        

        const apiZipCodes = response.data
          .filter(x => {
            const zipNum = parseInt(x.nr);
            return !zipCodeGroups.some(group => 
              zipNum >= group.from && zipNum <= group.to
            );
          })
          .map((x) => ({ 
            nr: x.nr, 
            navn: x.navn,
            isGroup: false as const 
          }));
        
        const matchingGroups = zipCodeGroups.filter(group => 
          group.displayName.toLowerCase().includes(query.toLowerCase()) ||
          group.from.toString().includes(query) ||
          group.to.toString().includes(query)
        );

        setZipCodes([...matchingGroups, ...apiZipCodes]);
      };
      fetchData();
    }
  }, [query, zipCodeGroups]);

  const handleChange = (event: any, newValues: (ZipCode | ZipCodeGroup)[]) => {
    onChange(newValues);
    setQuery("");
  };

  const renderMaterial = (variant: any) => (
    <div className="flex flex-col col-span-6">
      <Autocomplete
        className="!w-full"
        fullWidth={true}
        disablePortal
        size={style?.size}
        options={zipCodes}
        value={selectedZipCodes}
        noOptionsText="Søg efter postnummer eller bynavn"
        getOptionLabel={(option) => 
          option.isGroup ? option.displayName : `${option.nr} ${option.navn}`
        }
        onChange={handleChange}
        onInputChange={(event, newInputValue) => {
          setQuery(newInputValue);
        }}
        multiple={true}
        sx={{ width: 300 }}
        renderInput={(params) => (
          <TextField
            error={!!errors}
            helperText={errors ? errors[0] : undefined}
            variant={variant}
            {...params}
            label="Beliggenhed"
            required={true}
          />
        )}
      />
    </div>
  );

  switch (style?.type) {
    case "material-outlined":
      return renderMaterial("outlined");
    case "material-standard":
      return renderMaterial("standard");
    default:
      return renderMaterial("filled");
  }
};

export default ZipCodeInput;
